import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Maximize2, Radio, X } from "lucide-react";
import { api } from "@/lib/api";
import { useLiveIntelAccess } from "@/lib/liveIntel";
import { markReturn, takeReturn } from "@/lib/drawerReturn";
import { HOST, openLive } from "@/lib/liveDrawer";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LiveProfileBody } from "@/components/live/LiveProfileBody";

// v83 — the live-streamer profile as a wide side drawer, opened from anywhere with openLive(id).
function useEsc(onClose) {
  useEffect(() => {
    const k = (e) => e.key === "Escape" && !document.querySelector("[data-radix-popper-content-wrapper], [role=\"dialog\"]") && onClose();
    window.addEventListener("keydown", k);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = ""; };
  }, [onClose]);
}

export function LiveProfileDrawer({ id, onClose, onLeave }) {
  const nav = useNavigate();
  useEsc(onClose);
  const expand = () => { onClose(); nav(`/live-streaming-intel/${id}`); };
  return (
    <TooltipProvider>
      <div className="fixed inset-0 z-50" data-testid="live-drawer">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm c360-fade" onClick={onClose} data-testid="live-drawer-backdrop" />
        <aside className="absolute right-0 top-0 h-full overflow-y-auto border-l hairline c360-slide w-full lg:w-[min(1240px,92vw)]" style={{ background: "var(--bg, #0A0A0B)" }} data-testid="live-drawer-panel">
          <div className="sticky top-0 z-10 border-b hairline backdrop-blur-xl" style={{ background: "rgba(10,10,11,0.85)" }}>
            <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3">
              <span className="mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 inline-flex items-center gap-2"><Radio size={12} className="text-rose-400" /> Live streamer profile</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={expand} className="inline-flex h-8 w-8 items-center justify-center rounded-md border hairline text-zinc-300 hover:border-white/30" title="Open the full page" data-testid="live-drawer-expand"><Maximize2 size={14} /></button>
                <button type="button" onClick={onClose} className="h-8 px-3 inline-flex items-center gap-1.5 rounded-md border hairline text-sm text-zinc-200 hover:border-white/30" data-testid="live-drawer-close"><X size={14} /> Close</button>
              </div>
            </div>
          </div>
          <LiveProfileBody id={id} enabled inDrawer onLeave={onLeave} onClose={onClose} className="px-4 sm:px-6 py-6" />
        </aside>
      </div>
    </TooltipProvider>
  );
}

// Mounted once in AppShell.
export function LiveDrawerHost() {
  const access = useLiveIntelAccess();
  const loc = useLocation();
  const here = loc.pathname + loc.search;
  const [id, setId] = useState(null);
  const last = useRef(loc.pathname);
  useEffect(() => { HOST.subs.add(setId); return () => { HOST.subs.delete(setId); }; }, []);
  useEffect(() => {
    const back = takeReturn("live", here);
    if (back) setId(back);
    else if (last.current !== loc.pathname) setId(null);
    last.current = loc.pathname;
  }, [loc.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = useCallback(() => setId(null), []);
  const leave = useCallback(() => { markReturn("live", here, id); setId(null); }, [here, id]);
  if (!id || !access.enabled) return null;
  return createPortal(<LiveProfileDrawer id={id} onClose={close} onLeave={leave} />, document.body);
}

// "Live streaming profile →" — only for teams with Live Streaming Intel, only when this creator has one.
export function useHasLive(id) {
  const access = useLiveIntelAccess();
  const [has, setHas] = useState(false);
  useEffect(() => {
    setHas(false);
    if (!access.enabled || !id) return undefined;
    let alive = true;
    api.get(`/live-intel/has/${encodeURIComponent(id)}`).then((r) => { if (alive) setHas(!!r.data.live); }).catch(() => {});
    return () => { alive = false; };
  }, [id, access.enabled]);
  return access.enabled && has;
}

export const LIVE_LINK_TEXT = "Live streaming profile →";

export function LiveProfileLink({ id, onOpen, className = "", testid = "view-live-profile", green = false }) {
  const has = useHasLive(id);
  if (!has) return null;
  const tone = green ? "text-[#39FF14] hover:text-[#7CFF5C] font-medium text-sm" : "text-rose-300 hover:text-rose-200 text-xs";
  return (
    <button type="button" onClick={() => (onOpen ? onOpen() : openLive(id))} data-testid={testid}
      className={`inline-flex items-center gap-1.5 transition-colors ${tone} ${className}`}>
      <Radio size={12} /> {LIVE_LINK_TEXT}
    </button>
  );
}
